import {noop} from 'lodash';
import React, {FC, useState} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  CalendarsPanelId,
  ComposerPanelId,
  makeCalendarsPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {LightUserThemeProvider} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SchedulingLinkModel} from '../../../../../models/api/schedulingLinkModel';
import {MeetingTimesData} from '../../scheduling/meetingsettings/meetingSettingsPanel';
import {MeetingTimesDialogContainer} from '../../scheduling/meetingTimesDialogContainer';
import {ComposerSchedulingDropdownContainer} from '../dropdowns/composerSchedulingDropdownContainer';

/*
 * Types.
 */

export interface SchedulingToolbarButtonProps {
  panelId: ComposerPanelId | ViewerPanelId;
  onSchedulingLinkInsertMeetingTimes?: (data: MeetingTimesData) => void;
  onSchedulingLinkInsertLink?: (model: SchedulingLinkModel) => void;
}

/*
 * Component.
 */

export const SchedulingToolbarButton: FC<SchedulingToolbarButtonProps> = (props) => {
  const [insertMeetingTimesPanelId, setInsertMeetingTimesPanelId] = useState<CalendarsPanelId>();
  const onInsertMeetingTimesSelect = () => {
    // Check to see if a meeting times window is already open. If one is already
    // open, do not open a new one. Without this check, an existing meeting
    // times window will close, and a new one to not open. An attempt to focus
    // the existing window was made, but did not work due to browser
    // limitations: https://github.com/frontapp/front-client/pull/9841.
    if (!insertMeetingTimesPanelId) {
      setInsertMeetingTimesPanelId(makeCalendarsPanelId());
    }
  };
  const onInsertMeetingTimesDialogClose = () => setInsertMeetingTimesPanelId(undefined);

  return (
    <>
      <PopoverCoordinator
        renderButton={(isOpen) => renderButton(isOpen)}
        renderModal={(modalProps) => (
          <ComposerSchedulingDropdownContainer
            {...modalProps}
            panelId={props.panelId}
            onInsertMeetingTimesSelect={onInsertMeetingTimesSelect}
            onInsertMeetingTimes={props.onSchedulingLinkInsertMeetingTimes || noop}
            onInsertLink={props.onSchedulingLinkInsertLink || noop}
          />
        )}
      />
      {insertMeetingTimesPanelId && (
        <LightUserThemeProvider>
          <MeetingTimesDialogContainer
            onDialogClose={onInsertMeetingTimesDialogClose}
            onInsertMeetingTimes={(meetingTimesData) => {
              onInsertMeetingTimesDialogClose();
              if (props.onSchedulingLinkInsertMeetingTimes) {
                props.onSchedulingLinkInsertMeetingTimes(meetingTimesData);
              }
            }}
            panelId={insertMeetingTimesPanelId}
            parentPanelId={props.panelId}
          />
        </LightUserThemeProvider>
      )}
    </>
  );
};

function renderButton(isOpen: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          hasHover={isOpen}
          iconName="schedule"
          label={<Trans id="composer-scheduling">Scheduling</Trans>}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}
